import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideBanknote,
  lucideCalendar,
  lucideLandmark,
  lucideTriangleAlert,
  lucideWallet,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { KpiCardComponent } from '../../components/kpi-card/kpi-card.component';
import {
  CompanyGroupService,
  MemberRedemptionRecord,
  MemberSettlement,
} from '../../services/company-group.service';
import { CompanyService } from '../../services/company.service';
import { formatCurrency } from '../../utils/currency.utils';
import { berlinPeriod, parseApiDate, periodLabel } from '../../utils/period.utils';

type MonthStatus = 'running' | 'open' | 'in_settlement' | 'partly_paid' | 'paid';

interface MonthRow {
  period: string;
  label: string;
  count: number;
  redeemedCents: number;
  payoutCents: number;
  status: MonthStatus;
}

const MONTH_STATUS: Record<MonthStatus, { label: string; variant: 'solid' | 'outline' }> = {
  running: { label: 'Laufender Monat', variant: 'outline' },
  open: { label: 'Offen', variant: 'outline' },
  in_settlement: { label: 'In Abrechnung', variant: 'outline' },
  partly_paid: { label: 'Teilweise überwiesen', variant: 'outline' },
  paid: { label: 'Überwiesen', variant: 'solid' },
};

/**
 * A member's money view in place of the Stripe dashboard: what it redeemed per month,
 * what its network still owes and the monthly settlements (Sammelüberweisung) it got.
 */
@Component({
  selector: 'app-member-payouts',
  standalone: true,
  imports: [
    RouterLink,
    NgIcon,
    KpiCardComponent,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSkeletonImports,
    HlmTableImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideArrowRight,
      lucideBanknote,
      lucideCalendar,
      lucideLandmark,
      lucideTriangleAlert,
      lucideWallet,
    }),
  ],
  templateUrl: './member-payouts.component.html',
})
export class MemberPayoutsComponent {
  private readonly groupService = inject(CompanyGroupService);
  private readonly companyService = inject(CompanyService);

  readonly company = this.companyService.company;
  readonly networkName = computed(() => this.company()?.member_of_company_name || 'Deine Zentrale');

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  private readonly redemptions = signal<MemberRedemptionRecord[]>([]);
  readonly settlements = signal<MemberSettlement[]>([]);

  private readonly currentPeriod = berlinPeriod(new Date());

  /** Months with redemptions, newest first; the running month is always there. */
  readonly months = computed<MonthRow[]>(() => {
    const byPeriod = new Map<string, MemberRedemptionRecord[]>([[this.currentPeriod, []]]);
    for (const r of this.redemptions()) {
      const period = berlinPeriod(parseApiDate(r.redeemed_at));
      byPeriod.set(period, [...(byPeriod.get(period) ?? []), r]);
    }
    return [...byPeriod.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([period, rows]) => ({
        period,
        label: periodLabel(period),
        count: rows.length,
        redeemedCents: rows.reduce((sum, r) => sum + r.amount, 0),
        payoutCents: rows.reduce((sum, r) => sum + (r.payout_cents ?? 0), 0),
        status: this.monthStatus(period, rows),
      }));
  });

  readonly runningMonth = computed(
    () => this.months().find((m) => m.period === this.currentPeriod)!,
  );

  /** Redeemed but not paid yet: open, or in a settlement the network hasn't paid. */
  readonly outstandingCents = computed(() =>
    this.redemptions()
      .filter((r) => r.settlement_status !== 'paid')
      .reduce((sum, r) => sum + (r.payout_cents ?? 0), 0),
  );

  readonly visibleSettlements = computed(() =>
    this.settlements().filter((s) => s.status !== 'cancelled'),
  );

  readonly lastPaid = computed(
    () =>
      this.visibleSettlements()
        .filter((s) => s.status === 'paid')
        .sort((a, b) => (b.paid_at ?? '').localeCompare(a.paid_at ?? ''))[0] ?? null,
  );

  constructor() {
    forkJoin({
      stats: this.groupService.getMemberRedemptionStats(),
      settlements: this.groupService.getMySettlements(),
    }).subscribe({
      next: ({ stats, settlements }) => {
        this.redemptions.set(stats.redemptions);
        this.settlements.set(settlements);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err.error?.detail || 'Deine Auszahlungen konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  euro(cents: number | null | undefined): string {
    return `${formatCurrency(cents ?? 0)} €`;
  }

  date(iso: string | null): string {
    return iso ? parseApiDate(iso).toLocaleDateString('de-DE') : '–';
  }

  statusLabel(status: MonthStatus): string {
    return MONTH_STATUS[status].label;
  }

  statusVariant(status: MonthStatus): 'solid' | 'outline' {
    return MONTH_STATUS[status].variant;
  }

  lastPaidSubtext(): string | undefined {
    const s = this.lastPaid();
    return s ? `${s.period_label} · am ${this.date(s.paid_at)}` : undefined;
  }

  private monthStatus(period: string, rows: MemberRedemptionRecord[]): MonthStatus {
    const paid = rows.filter((r) => r.settlement_status === 'paid').length;
    if (rows.length > 0 && paid === rows.length) return 'paid';
    if (paid > 0) return 'partly_paid';
    if (rows.some((r) => r.settlement_status === 'draft' || r.settlement_status === 'exported')) {
      return 'in_settlement';
    }
    return period === this.currentPeriod ? 'running' : 'open';
  }
}
